import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

import { ScheduleService } from '../../../service/schedule/schedule.service';
import { CustomToastrService } from '../../../service/toastr/custom-toastr.service';
import { OfferProgram } from '../../../resource/offerProgram.resource';
import { Broadcast } from '../../../resource/broadcast.resource';
import { Cell } from '../../../resource/availability/cell.resource';

import { Subject } from 'rxjs';
import { Schedule } from '../../../resource/schedule/schedule.resource';
import * as moment from 'moment';

@Component({
  selector: 'app-program-card',
  templateUrl: 'program-card.component.html',
  styleUrls: ['program-card.component.scss']
})
export class ProgramCardComponent implements OnInit, OnDestroy, OnChanges {

  @Input() offerProgram: OfferProgram;
  @Input() broadcasts: Broadcast[];
  @Input() schedules: Schedule[]
  @Input() gridData: any[];
  @Input() filters: any;
  @Input() loading: boolean;

  public path: String;
  public logo: any;
  public channel: String;
  public comments: Array<String>;
  public maxNumberOfAdvertiser: number;
  public availableWeekSlotsCount: number = 0;
  public potentialRemainingAmount: number = null;

  public get shouldShowPotentialRemaining(): boolean {
    return !!this.offerProgram &&
      this.normalizeId(this.offerProgram.offer && this.offerProgram.offer.offerType &&
        this.offerProgram.offer.offerType.id) !== '3' &&
      !!(this.filters && (
        (this.filters.offers && this.filters.offers.length > 0) ||
        (this.filters.programs && this.filters.programs.length > 0)
      )) &&
      this.normalizeAreaId(this.offerProgram.area && this.offerProgram.area.id) === 'NAT' &&
      ['2', '3', '5'].indexOf(this.normalizeId(
        this.offerProgram.area && this.offerProgram.area.channel && this.offerProgram.area.channel.id
      )) >= 0 &&
      this.potentialRemainingAmount !== null &&
      this.potentialRemainingAmount !== 0;
  }

  destroyCommentsFromBroadcast$: Subject<boolean> = new Subject<boolean>();
  destroyList$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private scheduleService: ScheduleService,
    private customToastrService: CustomToastrService,
  ) {
    this.comments = [];
  }

  ngOnInit() {
    this.path = 'assets/images/logos/channels/';
    this.logo = this.getProgramLogo();
    this.getProgramChannel();
    this.maxNumberOfAdvertiser = this.getMaxNumberOfAdvertiser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // listener for schedules data
    if (changes.schedules && this.schedules) {
      this.schedules.forEach((schedule) => {
        if (schedule.comments) {
          this.comments.push(schedule.comments);
        }
      });

      // removing duplicate comments
      this.comments = this.distinctComments(this.comments);
    }

    if ((changes.offerProgram || changes.filters) && this.offerProgram) {
      this.maxNumberOfAdvertiser = this.getMaxNumberOfAdvertiser();
    }

    if (changes.offerProgram || changes.gridData || changes.filters) {
      this.availableWeekSlotsCount = this.getAvailableWeekSlotsCount();
      this.potentialRemainingAmount = this.getPotentialRemainingAmount();
    }
  }

  ngOnDestroy() {
    this.destroyCommentsFromBroadcast$.next(true);
    this.destroyList$.next(true);
    this.destroyCommentsFromBroadcast$.unsubscribe();
    this.destroyList$.unsubscribe();
  }

  /**
   * Remove duplicates from comments collection
   *
   * @param {Array<String>} comments
   * @returns {Array<String>}
   */
  private distinctComments(comments: Array<String>): Array<String> {
    return Array.from(new Set(comments));
  }

  /**
   * Get logo for program channel
   *
   * @returns {string}
   */
  private getProgramLogo(): string {
    if (this.offerProgram && this.offerProgram.area && this.offerProgram.area.channel && this.offerProgram.area.channel.image) {
      this.logo = this.offerProgram.area.channel.image.split('.');
      return this.logo[0] + '-G.' + this.logo[1];
    } else {
      return null;
    }
  }

  /**
   * Get channel for program
   */
  private getProgramChannel(): void {
    if (this.offerProgram && this.offerProgram.area && this.offerProgram.area.channel && this.offerProgram.area.channel.name) {
      this.channel = this.offerProgram.area.channel.name;
    }
  }

  private normalizeId(value: any): string {
    return value === null || value === undefined || value === '' ?
      null :
      String(value);
  }

  private normalizeAreaId(value: any): string {
    return this.normalizeId(value) || 'NAT';
  }

  private getAvailableWeekSlotsCount(): number {
    if (!Array.isArray(this.gridData) || !this.gridData.length ||
        !this.filters || this.filters.displayMode !== 'year' ||
        !this.filters.broadcasted_from || !this.filters.broadcasted_to ||
        !this.maxNumberOfAdvertiser || this.maxNumberOfAdvertiser < 0) {
      return 0;
    }

    const firstDate = moment.max(moment().startOf('day'), moment(this.filters.broadcasted_from));
    const lastDate = moment(this.filters.broadcasted_to);

    if (!firstDate.isValid() || !lastDate.isValid() || firstDate.isAfter(lastDate, 'day')) {
      return 0;
    }

    return this.gridData.reduce((total, column) => {
      if (!Array.isArray(column)) {
        return total;
      }

      // Purchase rows come first; option and alert rows must not add saleable places.
      const availablePurchaseCells = column.slice(0, this.maxNumberOfAdvertiser)
        .filter((cell) => cell && cell.type === Cell.AVAILABLE && Array.isArray(cell.broadcasts) &&
          cell.broadcasts.some((broadcast) => broadcast && broadcast.startTime &&
            moment(broadcast.startTime).isBetween(firstDate, lastDate, 'day', '[]')
          )
        );

      return total + availablePurchaseCells.length;
    }, 0);
  }

  private getPotentialRemainingAmount(): number {
    const weeklyBudget = this.getWeeklyBudget();

    return weeklyBudget === null ?
      null :
      weeklyBudget * this.availableWeekSlotsCount;
  }

  private getWeeklyBudget(): number {
    if (!this.offerProgram ||
        !this.offerProgram.offer ||
        !this.offerProgram.offerProgramBudgets ||
        !this.offerProgram.offerProgramBudgets.length ||
        this.offerProgram.offer.marketingBaseType !== 'Semaines') {
      return null;
    }

    const baseWeeks = Number(this.offerProgram.offer.marketingBaseNumber);
    const totalBudget = Number(this.offerProgram.offerProgramBudgets[0].price);

    if (baseWeeks !== 4 || isNaN(totalBudget)) {
      return null;
    }

    return totalBudget / baseWeeks;
  }

  private getMaxNumberOfAdvertiser(): number {
    const offerMax = this.offerProgram &&
      this.offerProgram.offer &&
      this.offerProgram.offer.maxNumberOfAdvertiser !== null &&
      this.offerProgram.offer.maxNumberOfAdvertiser !== undefined ?
      Number(this.offerProgram.offer.maxNumberOfAdvertiser) :
      null;

    if (!this.offerProgram ||
        !this.offerProgram.program ||
        !this.offerProgram.program.places ||
        !this.offerProgram.area ||
        !this.offerProgram.area.channel) {
      return offerMax;
    }

    const channelId = this.normalizeId(this.offerProgram.area.channel.id);
    const areaId = this.normalizeAreaId(this.offerProgram.area.id);
    const periodStart = this.filters && this.filters.broadcasted_from ?
      this.filters.broadcasted_from.substring(0, 10) : null;
    const periodEnd = this.filters && this.filters.broadcasted_to ?
      this.filters.broadcasted_to.substring(0, 10) : null;
    let places = this.offerProgram.program.places.filter((place) =>
      this.normalizeId(place.channelId) === channelId &&
      this.normalizeAreaId(place.areaId) === areaId
    );
    let placeLimit = null;

    if (!places.length) {
      places = this.offerProgram.program.places.filter((place) =>
        !this.normalizeId(place.channelId)
      );
    }

    places.filter((place) =>
      !periodStart || !periodEnd || !place.startTime || !place.endTime ||
      (place.startTime.substring(0, 10) <= periodEnd && place.endTime.substring(0, 10) >= periodStart)
    ).forEach((place) => {
      const placeValue = place.maxNumberOfAdvertiser !== null &&
        place.maxNumberOfAdvertiser !== undefined ?
        Number(place.maxNumberOfAdvertiser) :
        null;

      if (placeValue === null || isNaN(placeValue)) {
        return;
      }

      placeLimit = placeLimit === null ? placeValue : Math.min(placeLimit, placeValue);
    });

    if (offerMax === null) {
      return placeLimit;
    }

    return placeLimit === null ? offerMax : Math.min(offerMax, placeLimit);
  }
}
